import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { CreditsIcon } from 'src/components/Membership/MembershipIcon'
import type { ExtraPackageContentItem } from 'src/features/ExtraPackage/api/types'
import type { OneTimePackCardDecoration } from 'src/features/ExtraPackage/domain/oneTimeCardData'
import { partitionPackageContents } from 'src/features/ExtraPackage/domain/packageContents'
import type {
  getCardMeta,
  PackageContentMeta,
} from 'src/features/ExtraPackage/domain/packCardMeta'
import { useDecorationLabel } from 'src/features/UserDecoration/utils/decorationTypeLabel'
import type { Currency } from 'src/services/apollo/generated/graphql'
import { formatPrice } from 'src/utils/currency'

interface OrderSummaryProps {
  tierLabel: string
  isMembership: boolean
  isAnnual: boolean
  credits?: number
  baseCredits?: number
  currentPrice: { currency: Currency; amount: number } | null
  packageContents: ExtraPackageContentItem[]
  packCardMeta: ReturnType<typeof getCardMeta> | null
}

export const OrderSummary: FC<OrderSummaryProps> = ({
  tierLabel,
  isMembership,
  isAnnual,
  credits,
  baseCredits,
  currentPrice,
  packageContents,
  packCardMeta,
}) => {
  const { t } = useTranslation(['membership'])

  const packName = packCardMeta?.packNameI18nKey
    ? t(packCardMeta.packNameI18nKey, {
        defaultValue: packCardMeta.packName ?? '',
      })
    : (packCardMeta?.packName ?? '')

  const heading = isMembership
    ? tierLabel || t('membership:membership.label_long')
    : packName ||
      t('membership:payment.credit-pack', {
        defaultValue: 'Credit pack',
      })

  const { regular, decorations } = partitionPackageContents(packageContents)

  const formattedCurrentPrice = currentPrice
    ? formatPrice(currentPrice.currency, currentPrice.amount)
    : ''

  const cadenceSubLine = getCadenceSubLine({
    t,
    isMembership,
    isAnnual,
    formattedPrice: formattedCurrentPrice,
  })

  const totalLabel = isMembership
    ? t('membership:payment.total-due-today', {
        defaultValue: 'Total due today',
      })
    : t('membership:payment.order-summary.total')

  return (
    <div className="rounded-2xl bg-background-secondary p-5 sm:p-6">
      <h2 className="text-lg font-semibold mb-1">{heading}</h2>
      {isMembership && currentPrice && (
        <p className="text-sm opacity-60 tabular-nums">
          {t('membership:membership.label_long')} · {formattedCurrentPrice}{' '}
          {isAnnual
            ? t('membership:membership.price-tag.per-year')
            : t('membership:membership.price-tag.per-month')}
        </p>
      )}

      <div className="mt-4 space-y-2">
        {credits != null && credits > 0 && (
          <div className="flex items-center justify-between text-sm opacity-70">
            <span className="flex items-center gap-2">
              <CreditsIcon className="size-4 shrink-0" />
              {t('membership:landing-page.tab.extra-pack')}
            </span>
            <span className="tabular-nums">
              {baseCredits != null && (
                <span className="line-through opacity-50 mr-1.5">
                  {baseCredits.toLocaleString()}
                </span>
              )}
              {credits.toLocaleString()}
            </span>
          </div>
        )}

        {regular.map((row, idx) => {
          const md = (row.metadata ?? {}) as PackageContentMeta
          const labelFallback =
            md.displayName ?? md.label ?? md.name ?? row.kind
          const label = t(md.displayNameI18nKey ?? '', {
            defaultValue: labelFallback,
          })
          return (
            <div
              key={`${row.kind}-${idx}`}
              className="flex items-center justify-between text-sm opacity-70"
            >
              <span>{label}</span>
              <span className="tabular-nums">×{row.quantity ?? 0}</span>
            </div>
          )
        })}
        {decorations.map((deco, idx) => (
          <DecorationRow key={`decoration-${deco.code}-${idx}`} {...deco} />
        ))}
      </div>

      <div className="border-t border-card-border pt-4 mt-4">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-medium">{totalLabel}</span>
          <span className="text-base font-semibold tabular-nums">
            {currentPrice ? formattedCurrentPrice : '—'}
          </span>
        </div>
        <p className="text-xs opacity-60 mt-1">{cadenceSubLine}</p>
      </div>
    </div>
  )
}

const DecorationRow: FC<OneTimePackCardDecoration> = ({ code, quantity }) => {
  const label = useDecorationLabel(code)
  if (!label) return null
  return (
    <div className="flex items-center justify-between text-sm opacity-70">
      <span>{label}</span>
      <span className="tabular-nums">×{quantity ?? 0}</span>
    </div>
  )
}

function getCadenceSubLine({
  t,
  isMembership,
  isAnnual,
  formattedPrice,
}: {
  t: ReturnType<typeof useTranslation>['t']
  isMembership: boolean
  isAnnual: boolean
  formattedPrice: string
}) {
  if (!isMembership) {
    return t('membership:membership.price-tag.charged-once')
  }
  if (isAnnual) {
    return t('membership:membership.price-tag.yearly-notice', {
      price: formattedPrice,
      defaultValue: `Renews at ${formattedPrice} next year`,
    })
  }
  return t('membership:membership.price-tag.monthly-notice', {
    price: formattedPrice,
  })
}
